import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowRight, ShieldCheck, Key, RefreshCw, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const LoginPage = () => {
  const [step, setStep] = useState(1); // 1: Password, 2: OTP, 3: Forgot Request, 4: Reset Password
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [otp, setOtp] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const navigate = useNavigate();

  React.useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${constant.API_BASE_URL}/auth/login`, {
        email: formData.email,
        password: formData.password
      });

      if (response.data.otp_required) {
        setStep(2);
        setLoading(false);
      } else if (response.data.success) {
        const token = response.data.token?.accessToken || response.data.token || response.data.authToken;
        sessionStorage.setItem('adminToken', token);
        navigate('/');
      } else {
        setError(response.data.message || 'Login failed. Please check your credentials.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors || 'Server error. Please try again later.');
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${constant.API_BASE_URL}/auth/verify-login-otp`, {
        email: formData.email,
        otp: otp
      });

      if (response.data.success) {
        const token = response.data.token?.accessToken || response.data.token || response.data.authToken;
        sessionStorage.setItem('adminToken', token);
        navigate('/');
      } else {
        setError(response.data.message || 'Invalid or expired OTP code.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP code.');
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    setError('');
    setSuccessMsg('');
    try {
      const response = await axios.post(`${constant.API_BASE_URL}/auth/resend-login-otp`, {
        email: formData.email
      });
      if (response.data.success) {
        setSuccessMsg('OTP code resent successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setResending(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      const response = await axios.post(`${constant.API_BASE_URL}/auth/forgot-password`, {
        email: resetEmail
      });

      if (response.data.success) {
        setSuccessMsg(response.data.message || 'OTP sent to your email.');
        setStep(4);
      } else {
        setError(response.data.message || 'Reset request failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      // 1. Verify OTP
      const checkRes = await axios.post(`${constant.API_BASE_URL}/auth/check-otp`, {
        email: resetEmail,
        otp: resetOtp
      });

      if (checkRes.data.success) {
        // 2. Reset Password
        const resetRes = await axios.put(`${constant.API_BASE_URL}/auth/reset-password/user`, {
          email: resetEmail,
          password: newPassword
        });

        if (resetRes.data.success) {
          setSuccessMsg('Password updated successfully! Please sign in.');
          setStep(1);
          setResetEmail('');
          setResetOtp('');
          setNewPassword('');
        } else {
          setError(resetRes.data.message || 'Failed to update password.');
        }
      } else {
        setError('Incorrect or expired OTP code.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-['DM_Sans']">
      <div className="max-w-md w-full">
        {/* Logo / Brand Area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-pink-600 text-white shadow-lg shadow-pink-200 mb-4">
            <img src="/xoxo-logo.jpeg" alt="Logo" className="w-full h-full object-cover rounded-2xl" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Admin Portal</h1>
          <p className="text-slate-500 mt-2">Sign in to manage XOXO streaming</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 p-8 border border-slate-100">
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm font-medium flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              {error}
            </div>
          )}

          {successMsg && (
            <div className="mb-6 p-4 bg-green-50 border border-green-100 text-green-600 rounded-xl text-sm font-medium flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              {successMsg}
            </div>
          )}

          {/* STEP 1: PASSWORD LOGIN */}
          {step === 1 && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <Mail size={18} />
                  </div>
                  <input
                    type="text"
                    name="email"
                    required
                    className="block w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                    placeholder="admin@xoxo.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <Lock size={18} />
                  </div>
                  <input
                    type="password"
                    name="password"
                    required
                    className="block w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500" />
                  <span className="text-slate-600 font-medium">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setStep(3);
                    setError('');
                    setSuccessMsg('');
                  }}
                  className="text-pink-600 font-bold hover:underline"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all hover:shadow-lg hover:shadow-slate-200 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    Sign In <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* STEP 2: LOGIN OTP VERIFICATION */}
          {step === 2 && (
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div className="text-center mb-4">
                <p className="text-slate-600 text-sm">
                  We've sent a 6-digit OTP code to <strong className="text-slate-800">{formData.email}</strong>.
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">OTP Verification Code</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <Key size={18} />
                  </div>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    className="block w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 tracking-[0.5em] font-bold text-center text-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setOtp('');
                    setError('');
                    setSuccessMsg('');
                  }}
                  className="flex items-center gap-1 text-slate-500 font-bold hover:underline"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                <button
                  type="button"
                  disabled={resending}
                  onClick={handleResendOtp}
                  className="flex items-center gap-1.5 text-pink-600 font-bold hover:underline disabled:opacity-50"
                >
                  <RefreshCw size={14} className={resending ? 'animate-spin' : ''} /> Resend OTP
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all hover:shadow-lg hover:shadow-slate-200 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    Verify OTP & Sign In <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* STEP 3: FORGOT PASSWORD REQUEST */}
          {step === 3 && (
            <form onSubmit={handleForgotSubmit} className="space-y-5">
              <div className="text-center mb-4">
                <p className="text-slate-500 text-sm">
                  Enter your email address and we'll send you an OTP code to reset your password.
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Registered Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    required
                    className="block w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                    placeholder="admin@xoxo.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-start">
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setError('');
                    setSuccessMsg('');
                  }}
                  className="flex items-center gap-1 text-slate-500 font-bold hover:underline text-sm"
                >
                  <ArrowLeft size={16} /> Back to Sign In
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all hover:shadow-lg hover:shadow-slate-200 active:scale-[0.98]"
              >
                {loading ? (
                  <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    Send Reset OTP <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* STEP 4: RESET PASSWORD WITH OTP */}
          {step === 4 && (
            <form onSubmit={handleResetSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">OTP Verification Code</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <Key size={18} />
                  </div>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    placeholder="000000"
                    className="block w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-center tracking-[0.5em] font-bold text-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <Lock size={18} />
                  </div>
                  <input
                    type="password"
                    required
                    placeholder="Min 6 characters"
                    className="block w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-start">
                <button
                  type="button"
                  onClick={() => {
                    setStep(3);
                    setError('');
                    setSuccessMsg('');
                  }}
                  className="flex items-center gap-1 text-slate-500 font-bold hover:underline text-sm"
                >
                  <ArrowLeft size={16} /> Back
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all hover:shadow-lg hover:shadow-slate-200 active:scale-[0.98]"
              >
                {loading ? (
                  <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    Reset Password & Sign In <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-slate-400 text-sm">
              Trouble signing in? <a href="mailto:support@xoxo.com" className="text-slate-600 font-bold hover:underline">Contact Support</a>
            </p>
          </div>
        </div>

        <p className="text-center text-slate-400 text-xs mt-8">
          &copy; 2026 XOXO Live Streaming. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;